// Batch-платёж по нескольким долгам разом. Открывается с экрана «Долги»
// кнопкой «Оплатить ежемесячные». По умолчанию выбраны все долги с
// monthly_payment > 0, сумма каждого = monthly_payment, можно снять галочку
// или поправить сумму на месте.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { type BatchPaymentItem, api } from '../api/client';
import type { Debt } from '../api/types';
import { haptic } from '../lib/telegram';
import { humanError } from '../lib/errors';
import { useToast } from '../lib/toast';
import { SheetCta } from './SheetCta';
import { useMainButton } from '../lib/use-main-button';
import { useViewCurrency } from '../lib/view-currency';

type Props = {
  debts: Debt[];
  onClose: () => void;
};

type RowState = {
  debtId: string;
  selected: boolean;
  amountMajor: string;
};

function parseAmountToMinor(raw: string): bigint | null {
  const cleaned = raw.replace(/\s/g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  const [intPart, fracPart = ''] = cleaned.split('.');
  const padded = (fracPart + '00').slice(0, 2);
  const combined = `${intPart}${padded}`.replace(/^0+/, '') || '0';
  return BigInt(combined);
}

function makeIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `bulk-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function PayMonthlySheet({ debts, onClose }: Props): JSX.Element {
  const { formatMoney } = useViewCurrency();
  // Берём только долги с monthly_payment > 0 (по personal/company без графика — нечего автоматически оплачивать).
  const payable = useMemo(() => debts.filter((d) => BigInt(d.monthly_payment_minor) > 0n), [debts]);

  const [rows, setRows] = useState<RowState[]>(() =>
    payable.map((d) => ({
      debtId: d.id,
      selected: true,
      amountMajor: (BigInt(d.monthly_payment_minor) / 100n).toString(),
    })),
  );

  // Локальный лок на клик MainButton.
  const [submitting, setSubmitting] = useState(false);
  // Ключ генерим один при открытии sheet — повторный submit с тем же ключом
  // (например, при ретрае после network-сбоя) не создаст дублей событий.
  const [batchKey] = useState(makeIdempotencyKey());

  const queryClient = useQueryClient();
  const toast = useToast();
  const batchMut = useMutation({
    mutationFn: (items: BatchPaymentItem[]) => api.payMonthlyBatch(items, batchKey),
    onSuccess: (data) => {
      const failed = data.results.filter((r) => !r.ok);
      if (failed.length > 0) {
        haptic('warning');
      } else {
        haptic('success');
      }
      queryClient.invalidateQueries({ queryKey: ['debts'] });
      queryClient.invalidateQueries({ queryKey: ['kpi'] });
      queryClient.invalidateQueries({ queryKey: ['projection'] });
      onClose();
    },
    onError: (e) => toast.error(humanError(e, 'Не удалось отметить платежи')),
    onSettled: () => setSubmitting(false),
  });

  // Список платежей, готовых отправить: selected & валидная сумма > 0.
  // period_ym для каждого: первый просроченный, либо next_due. Если ни того ни
  // другого — отправляем без period, событие будет просто без привязки к месяцу.
  const itemsToSend = useMemo<BatchPaymentItem[]>(() => {
    const out: BatchPaymentItem[] = [];
    for (const row of rows) {
      if (!row.selected) continue;
      const minor = parseAmountToMinor(row.amountMajor);
      if (!minor || minor <= 0n) continue;
      const debt = payable.find((d) => d.id === row.debtId);
      if (!debt) continue;
      const isPrepayment = minor > BigInt(debt.monthly_payment_minor);
      const periodYm = isPrepayment
        ? null
        : debt.overdue_periods?.[0] ?? debt.next_due_date?.slice(0, 7) ?? null;
      out.push({
        debt_id: row.debtId,
        amount_minor: minor.toString(),
        is_prepayment: isPrepayment,
        period_ym: periodYm,
      });
    }
    return out;
  }, [rows, payable]);

  const totalMinor = useMemo(
    () => itemsToSend.reduce((acc, i) => acc + BigInt(i.amount_minor), 0n),
    [itemsToSend],
  );

  const busy = submitting || batchMut.isPending;

  const cta = useMainButton({
    text: `Оплатить ${itemsToSend.length} долг${itemsToSend.length === 1 ? '' : 'ов'}: ${formatMoney(totalMinor, 'UZS')}`,
    onClick: () => {
      if (busy || itemsToSend.length === 0) return;
      setSubmitting(true);
      batchMut.mutate(itemsToSend);
    },
    enabled: itemsToSend.length > 0 && !busy,
    progress: busy,
  });

  // Запрещаем закрывать через бэкдроп когда идёт submit — иначе можно
  // потерять состояние, а отправка уже улетела.
  useEffect(() => {
    if (busy) document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [busy]);

  const updateRow = (debtId: string, patch: Partial<RowState>): void => {
    setRows((prev) => prev.map((r) => (r.debtId === debtId ? { ...r, ...patch } : r)));
  };

  return (
    <div className="sheet-backdrop" onClick={busy ? undefined : onClose} role="presentation">
      <div className="sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="sheet-handle" />
        <div className="sheet-title">Оплатить ежемесячные</div>

        <div className="sheet-stat-grid">
          <div className="sheet-stat">
            <div className="sheet-stat-label">Выбрано</div>
            <div className="sheet-stat-value">
              {itemsToSend.length} / {payable.length}
            </div>
          </div>
          <div className="sheet-stat">
            <div className="sheet-stat-label">Итого</div>
            <div className="sheet-stat-value">{formatMoney(totalMinor, 'UZS')}</div>
          </div>
        </div>

        <div className="bulk-list">
          {payable.map((d) => {
            const row = rows.find((r) => r.debtId === d.id);
            if (!row) return null;
            return (
              <div key={d.id} className={`bulk-row ${row.selected ? '' : 'dim'}`}>
                <label className="bulk-row-check">
                  <input
                    type="checkbox"
                    checked={row.selected}
                    onChange={(e) => updateRow(d.id, { selected: e.target.checked })}
                    disabled={busy}
                  />
                </label>
                <div className="bulk-row-body">
                  <div className="bulk-row-title">{d.title}</div>
                  <div className="bulk-row-hint">
                    остаток {formatMoney(d.principal_remaining_minor, d.currency)}
                  </div>
                </div>
                <input
                  type="text"
                  inputMode="decimal"
                  className="bulk-row-amount"
                  value={row.amountMajor}
                  onChange={(e) => updateRow(d.id, { amountMajor: e.target.value })}
                  disabled={busy || !row.selected}
                  aria-label={`Сумма платежа по ${d.title}`}
                />
              </div>
            );
          })}
        </div>

        <div className="sheet-actions">
          <SheetCta {...cta} />
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
}
